import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const PAIN_POINTS = [
  {
    title: "High tenant turnover means constant re-marketing",
    description:
      "Rental properties cycle faster than sales. Every void period costs your landlord money and costs you time. Video gets properties in front of tenants faster, reducing void periods and keeping landlords happy.",
  },
  {
    title: "Landlords expect more for their management fee",
    description:
      "The days of a Rightmove listing and a phone call are over. Landlords want to see proactive marketing. Showing them a branded video ad for their property at the next review meeting changes the conversation entirely.",
  },
  {
    title: "Rental listings get less marketing attention",
    description:
      "Be honest: your sales team gets the photography budget and the videographer. Lettings gets a phone photo and a paragraph of text. QuiteRight means every rental listing gets the same professional video treatment as your premium sales instructions.",
  },
  {
    title: "Student lets need fast turnaround",
    description:
      "Student rental cycles are brutal. You have a window of weeks to fill an entire portfolio. Video marketing on Instagram and TikTok reaches students where they actually spend time, not on Rightmove.",
  },
]

const FEATURES = [
  {
    title: "Listing import from all major portals",
    description: "Paste a Rightmove, Zoopla, or OnTheMarket URL. QuiteRight pulls the photos, details, and key features automatically. Works for rental listings just as well as sales.",
  },
  {
    title: "Multi-platform video formats",
    description: "Every video comes ready for Instagram Reels, TikTok, YouTube Shorts, and Facebook. Portrait, landscape, and square. No re-editing, no reformatting.",
  },
  {
    title: "AI voiceover and scripts",
    description: "QuiteRight generates a letting-specific script from your listing details and adds a professional AI voiceover. Multiple voices and styles to match your brand.",
  },
  {
    title: "Your branding on every video",
    description: "Upload your logo once. Every video includes your agency branding, colours, and contact details. Consistent across your entire rental portfolio.",
  },
  {
    title: "Batch production at scale",
    description: "50 rental listings to market this month? Create 50 videos without breaking a sweat. Each one takes about 60 seconds.",
  },
  {
    title: "Share directly to social media",
    description: "Connect your Instagram, Facebook, TikTok, or YouTube accounts. Publish videos directly from QuiteRight without downloading and re-uploading.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Does QuiteRight work for rental listings?",
    a: "Absolutely. QuiteRight works with any property listing from Rightmove, Zoopla, or OnTheMarket, whether it's for sale or to let. The generated scripts adapt to rental-specific language automatically.",
  },
  {
    q: "Can I use QuiteRight for HMO and student let marketing?",
    a: "Yes. HMOs, student lets, and room-by-room rentals all work. Paste the listing URL and QuiteRight handles the rest. For student properties, video on TikTok and Instagram Reels is particularly effective since that's where your target tenants spend their time.",
  },
  {
    q: "How does video marketing help reduce void periods?",
    a: "Video listings get significantly more engagement on social media than static images. More eyes on your property means more enquiries, faster viewings, and shorter void periods. For landlords, that translates directly into reduced lost rent.",
  },
  {
    q: "What do I show landlords to justify the cost?",
    a: "Show them the video. Seriously. When a landlord sees a professional, branded video ad for their property being shared on Instagram and Facebook, the conversation about your management fee becomes much easier. It's tangible proof of proactive marketing.",
  },
  {
    q: "How much does QuiteRight cost for letting agents?",
    a: "Plans start at \u00A349/month for self-service video creation. The Managed plan at \u00A3299/month covers up to 50 videos and includes social media publishing. There's no separate pricing for lettings vs sales.",
  },
]

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function ForLettingAgents({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: meta.title,
            description: meta.description,
            url: meta.canonical,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "For Letting Agents" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                For letting agents
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                Every rental deserves a video, not just the premium ones
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                Reduce void periods, impress landlords, and reach tenants where they
                actually spend time. Professional video marketing for your entire
                rental portfolio in seconds.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-left-2">
                <p className="text-xs font-medium text-warm-grey">Reduce void periods</p>
                <p className="font-display text-xl font-bold text-white">with video</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The challenge
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Why lettings needs video more than sales
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2">
            {PAIN_POINTS.map((item, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <p className="font-display text-lg font-bold text-charcoal">{item.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.description}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Built for lettings
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Everything you need, nothing you don&rsquo;t
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <p className="font-display text-base font-bold text-charcoal">{feature.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate">{feature.description}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The numbers
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-white md:text-4xl">
              Video marketing for lettings, in numbers
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="font-display text-3xl font-bold text-white">60s</p>
                <p className="mt-2 text-sm text-warm-grey">per video, from listing URL to finished ad</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-red/30 bg-red/10 p-6 text-center">
                <p className="font-display text-3xl font-bold text-white">50+</p>
                <p className="mt-2 text-sm text-warm-grey">rental videos per month on the Managed plan</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="font-display text-3xl font-bold text-red">&pound;5.98</p>
                <p className="mt-2 text-sm text-warm-grey">per video, less than a tenant referencing fee</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <dl className="divide-y divide-stone">
                {FAQ.map((item, i) => (
                  <details key={i} className="group">
                    <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
                      <dt className="text-sm font-semibold leading-relaxed text-charcoal">
                        {item.q}
                      </dt>
                      <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="size-3">
                          <path d="M8 3v10M3 8h10" />
                        </svg>
                      </span>
                    </summary>
                    <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
                  </details>
                ))}
              </dl>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <RelatedPages links={relatedFor("for/letting-agents")} />

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Fill vacancies faster with video
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Every rental listing deserves professional video marketing.
              QuiteRight makes it possible without a production budget.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
